import { modals } from '@mantine/modals'
import type { ComponentType } from 'react'
import {
  LuCircleAlert,
  LuCircleCheck,
  LuInfo,
  LuTriangleAlert,
} from 'react-icons/lu'
import type { ReactNode } from 'react'
import { BUTTON_BASE, BUTTON_VARIANTS } from '../components/Button/variants'

/**
 * The app's blocking-dialog API, backed by Mantine's modal manager.
 *
 * Replaces sweetalert2. Its `Swal.fire` was imperative and callable from plain
 * hooks, which is why the modal *manager* is used here rather than rendering
 * `<Modal>` elements — the ten call sites are all inside callbacks, not render
 * bodies, and none of them own a piece of state to drive a component. (Ten
 * calls, not ten screens: one of them is `showPremiumLockPrompt`, which six
 * surfaces share.)
 *
 * Everything below the API is presentation, and it exists because the manager
 * renders ONE Mantine `<Modal>` for every dialog the app opens: there is no
 * component to put a className on, so the chrome has to be described here,
 * once. `CHROME` reproduces `components/Modal` — the panel every other modal
 * in the app renders — so a dialog is not a second modal look.
 */

/** Two dialogs pass markup built from server data; see `body` below. */
type DialogBody = { text?: string; html?: string }

/**
 * The severity mark, as the app's colour vocabulary rather than Mantine's.
 *
 * The four Mantine palette shades this used (`green-6`, `red-6`, `blue-6`,
 * `yellow-6`) were the last off-system colours in the module, and one of them
 * had nowhere to land: the token set has no blue. So `info` is the *absence*
 * of a hue — a neutral chip — which still separates it from `warning`, which
 * is the distinction the quality notice needs (`hasBlockingFinding`).
 *
 * The rest follow the system's meanings: `--primary` is "this went well",
 * `--error-text` is "this is wrong" (never `--error`, which is an answer-choice
 * fill and illegible as a glyph), `--secondary` is the advisory amber.
 */
type DialogIcon = 'success' | 'error' | 'info' | 'warning'

const ICONS: Record<
  DialogIcon,
  { icon: ComponentType<{ size?: number }>; className: string }
> = {
  success: {
    icon: LuCircleCheck,
    className: 'bg-[rgba(var(--primary),0.12)] text-primary',
  },
  error: {
    icon: LuCircleAlert,
    className: 'bg-[rgba(var(--error-text),0.12)] text-error-text',
  },
  warning: {
    icon: LuTriangleAlert,
    className: 'bg-[rgba(var(--secondary),0.14)] text-secondary',
  },
  info: {
    icon: LuInfo,
    className:
      'bg-[rgba(var(--over-primary-background),0.06)] text-text-secondary',
  },
}

/**
 * The panel, matched to `components/Modal` rather than to Mantine's defaults.
 *
 * `inner`'s `p-4` is the viewport gutter, and it is load-bearing: the app
 * baseline zeroes padding on every element, so without it the panel touches
 * the screen edge on a phone. `practice-layout.spec.ts` asserts the 16px on
 * both sides of the premium prompt.
 */
const CHROME = {
  inner: 'p-4',
  // `min(…,100%)` and not a bare `440px`: Mantine sizes the panel with
  // `flex: 0 0 var(--modal-size)` and relies on its own `max-width: 100%` to
  // shrink it, which naming a max-width here replaces. Without the `100%` the
  // panel stayed 440px wide inside a 373px column on a phone and hung over
  // both edges of the screen.
  content:
    'max-w-[min(440px,100%)] rounded-xl border border-[rgba(var(--over-primary-background),0.14)] bg-primary-background text-over-primary-background shadow-none',
  header: 'items-start gap-3 bg-transparent px-[22px] pt-[20px] pb-0',
  title: 'flex min-w-0 items-center gap-[10px]',
  close:
    'h-8 w-8 shrink-0 rounded-md text-text-secondary/70 hover:bg-[rgba(var(--over-primary-background),0.06)] hover:text-over-primary-background',
  body: 'px-[22px] pt-[10px] pb-[20px]',
}

/** `animate-fade-in-bottom`, as the transition object Mantine's Modal takes. */
const TRANSITION = {
  transitionProps: {
    duration: 200,
    transition: {
      in: { opacity: 1, transform: 'translateY(0)' },
      out: { opacity: 0, transform: 'translateY(10px)' },
      transitionProperty: 'opacity, transform',
    },
  },
} as const

const SHARED = {
  centered: true,
  withCloseButton: true,
  closeButtonProps: { 'aria-label': 'Închide' },
  overlayProps: { backgroundOpacity: 0.55, color: '#000' },
  classNames: CHROME,
  ...TRANSITION,
} as const

const titleWithIcon = (title: string, icon?: DialogIcon) => {
  const mark = icon === undefined ? null : ICONS[icon]
  return (
    <>
      {mark ? (
        <span
          className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md text-[13px] ${mark.className}`}
        >
          <mark.icon />
        </span>
      ) : null}
      {/* Its own element so the accessible name of the dialog is the title and
          nothing else — `practice-layout.spec.ts` finds the premium prompt by
          it, and measures this span's inset from the panel edge. */}
      <span className="min-w-0 text-[17px] leading-[1.35] font-semibold text-over-primary-background">
        {title}
      </span>
    </>
  )
}

/**
 * sweetalert2 accepted an `html` string and rendered it as markup. Two call
 * sites rely on that — the similar-grid link and the quality notice — and both
 * build their markup from server data, so it stays behind an explicit `html`
 * field rather than being conflated with `text`.
 *
 * Server markup arrives with no classes of its own, so the container styles it:
 * links in the accent rather than the browser's blue, and lists with the
 * bullets Tailwind's preflight strips. The link affordance is a hairline and
 * not an underline because `app-baseline.css` sets
 * `a { text-decoration: none !important }` in an earlier cascade layer, which
 * an `underline` utility cannot beat — important declarations sort layers in
 * reverse.
 */
const body = ({ text, html }: DialogBody): ReactNode => {
  const className =
    'text-[13.5px] leading-[1.55] text-text-secondary [&_ul]:list-disc [&_ul]:pl-[18px] [&_li]:mt-[3px] [&_a]:border-b [&_a]:border-[rgba(var(--primary),0.45)] [&_a]:text-primary'

  if (html !== undefined) {
    return (
      <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
    )
  }
  return <div className={className}>{text}</div>
}

/** The one row of actions both dialogs end on. */
const actions = (children: ReactNode) => (
  <div className="mt-[18px] flex flex-wrap justify-end gap-[8px]">
    {children}
  </div>
)

const action = (
  variant: 'primary' | 'secondary',
  label: string,
  onClick: () => void,
) => (
  <button
    type="button"
    className={`${BUTTON_VARIANTS[variant]} ${BUTTON_BASE} hover:brightness-95`}
    onClick={onClick}
  >
    {label}
  </button>
)

let counter = 0
const nextId = () => {
  counter += 1
  return `dialog-${counter}`
}

interface AlertOptions extends DialogBody {
  title: string
  icon?: DialogIcon
  /**
   * The dismiss label. Every call site says the same thing, so the module owns
   * it; pass one only to say something else.
   */
  confirmText?: string
}

/**
 * Something already happened and there is nothing to decide — the dialog only
 * has to be read and dismissed.
 *
 * It always renders that dismiss button now. The buttonless shape it used to
 * have existed for the two confirmations that closed themselves on a timer,
 * and those are toasts: a modal that covers the page to say "sent, thanks" and
 * then leaves is a confirmation of a click the user just made. Same reasoning
 * as `useGridFeedback.ts`, which dropped its own copy of that dialog.
 */
export const alertDialog = ({
  title,
  text,
  html,
  icon,
  confirmText = 'Am înțeles',
}: AlertOptions) => {
  const id = nextId()
  modals.open({
    ...SHARED,
    modalId: id,
    title: titleWithIcon(title, icon),
    children: (
      <>
        {body({ text, html })}
        {actions(
          action('primary', confirmText, () => {
            modals.close(id)
          }),
        )}
      </>
    ),
  })
}

interface ConfirmOptions extends DialogBody {
  title: string
  icon?: DialogIcon
  confirmText: string
}

/**
 * Resolves true when the user confirms — the shape `Swal.fire(...).then(r =>
 * r.isConfirmed)` had at both call sites. Dismissing resolves false, which is
 * what Swal did for both the deny button and a click outside.
 *
 * Built on `modals.open` rather than `openConfirmModal` so both dialogs share
 * one footer: the manager's own confirm modal lays its buttons out with
 * Mantine's `Group` and `Button`, which is a fourth button weight the design
 * system does not have.
 *
 * The cancel label is not a parameter. One of the two call sites said "Cancel"
 * and the other "Anulează", in a Romanian app; there is one word for it now.
 */
export const confirmDialog = ({
  title,
  text,
  html,
  icon,
  confirmText,
}: ConfirmOptions): Promise<boolean> =>
  new Promise(resolve => {
    const id = nextId()
    const settle = (confirmed: boolean) => () => {
      resolve(confirmed)
      modals.close(id)
    }

    modals.open({
      ...SHARED,
      modalId: id,
      title: titleWithIcon(title, icon),
      // Reached by the close button, the backdrop and Escape, and also after
      // either button above; an already-settled promise ignores this.
      onClose: () => {
        resolve(false)
      },
      children: (
        <>
          {body({ text, html })}
          {actions(
            <>
              {action('secondary', 'Anulează', settle(false))}
              {action('primary', confirmText, settle(true))}
            </>,
          )}
        </>
      ),
    })
  })
